import React, {
	useCallback,
	useMemo,
	useLayoutEffect,
	useEffect,
	type MouseEvent,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';
import { withRecoverableErrorBoundary } from '@atlassian/jira-error-boundaries';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import SearchNoResultsImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/empty/components/search-no-results';
import { useIntl } from '@atlassian/jira-intl';
import { FireUiAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { stopCaptureGraphQlErrors, type ErrorWrapper } from '@atlassian/jira-relay-errors';

import NoIssuesGlyph from '../../../common/ui/empty-list-icon/assets/no-issues-glyph.svg';
import { useIssueSearchQuery } from '../../../services/issue-search-query';
import messages from './messages';

type Props = {
	error?: Error;
	onMount?: (error: Error) => void;
	// TODO Remove prop when cleaning up nin.page-load-instrumentation-fix
	onCaughtIssueSearchError?: (error?: Error) => void;
};

type ErrorAttributes = {
	operationName: string;
	requestId: string;
	path: string;
	shortMessage: string;
	errorSource: string;
	errorType: string;
	statusCode: string;
	classification: string;
};

const ErrorViewNew = ({ onCaughtIssueSearchError, onMount, error: errorProp }: Props) => {
	const ISSUE_NAVIGATOR_MAIN_OPERATION_NAME = 'main_IssueNavigatorQuery';
	const ISSUE_NAVIGATOR_REFETCH_OPERATION_NAME = 'IssueNavigatorResultsRefetchQuery';
	const { formatMessage } = useIntl();
	const { onIssueSearchRefresh, fetchKey } = useIssueSearchQuery();

	const captureErrorInfo = useCallback(() => {
		const mapErrorToAttributes = (errorWrapper: ErrorWrapper): ErrorAttributes | undefined => {
			if (!errorWrapper) return undefined;
			const { meta, errors } = errorWrapper;
			if (errors && errors.length > 0) {
				const { message, path, extensions } = errors[0];
				return {
					operationName: meta.operationName,
					requestId: meta.requestId ?? '',
					path: path?.join('.') ?? '',
					shortMessage: message,
					errorSource: extensions?.errorSource ?? '',
					errorType: extensions?.errorType ?? '',
					statusCode: extensions?.statusCode ?? '',
					classification: extensions?.classification ?? '',
				};
			}
			return undefined;
		};
		const fullError = errorProp || new Error('There was an error searching for issues');
		if (fetchKey) {
			const capturedGQLErrorsInfo = stopCaptureGraphQlErrors(fetchKey);
			const validGQLErrorsInfo = capturedGQLErrorsInfo.find(
				(err) =>
					err.meta.operationName === ISSUE_NAVIGATOR_MAIN_OPERATION_NAME ||
					err.meta.operationName === ISSUE_NAVIGATOR_REFETCH_OPERATION_NAME,
			);

			if (validGQLErrorsInfo?.errors.length) {
				return {
					attributes: mapErrorToAttributes(validGQLErrorsInfo),
					fullError: new Error(
						`Issue Results Graphql Error(s): ${JSON.stringify(validGQLErrorsInfo)}`,
					),
				};
			}
		}
		return { fullError };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onClick = useCallback(
		(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			// @ts-expect-error - Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'.
			fireUIAnalytics(analyticsEvent.clone(), 'refreshFromError');
			onIssueSearchRefresh();
		},
		[onIssueSearchRefresh],
	);

	useEffect(() => {
		if (ff('nin.page-load-instrumentation-fix')) {
			const { fullError: error, attributes } = captureErrorInfo();
			onMount && onMount(error);
			fireErrorAnalytics({
				meta: {
					id: 'issueResultsErrorView',
					teamName: 'empanada',
					packageName: 'jiraIssueNavigator',
				},
				attributes,
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (!ff('nin.page-load-instrumentation-fix')) {
			const { fullError: error, attributes } = captureErrorInfo();
			onCaughtIssueSearchError && onCaughtIssueSearchError(error);
			fireErrorAnalytics({
				meta: {
					id: 'issueResultsErrorView',
					teamName: 'empanada',
					packageName: 'jiraIssueNavigator',
				},
				attributes,
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [captureErrorInfo, onCaughtIssueSearchError]);

	return (
		<Container>
			<EmptyState
				header={formatMessage(messages.emptyStateErrorHeader)}
				description={formatMessage(messages.emptyStateErrorDescription)}
				imageUrl={ff('em-enghealth-consolidate-jfe-assets') ? undefined : NoIssuesGlyph}
				testId="issue-navigator.ui.issue-results.error-view.error-message"
				primaryAction={
					<Button appearance="primary" onClick={onClick}>
						{formatMessage(messages.emptyStateErrorAction)}
					</Button>
				}
				renderImage={() =>
					ff('em-enghealth-consolidate-jfe-assets') ? (
						<Box xcss={ImageWrapperStyles}>
							<SearchNoResultsImage alt="" />
						</Box>
					) : undefined
				}
			/>
			<FireUiAnalytics action="viewed" actionSubject="screen" actionSubjectId="issueSearchError" />
		</Container>
	);
};

const ErrorViewOld = ({ onCaughtIssueSearchError, onMount, error: errorProp }: Props) => {
	const { formatMessage } = useIntl();
	const { onIssueSearchRefresh } = useIssueSearchQuery();

	const onClick = useCallback(
		(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			// @ts-expect-error - Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'.
			fireUIAnalytics(analyticsEvent.clone(), 'refreshFromError');
			onIssueSearchRefresh();
		},
		[onIssueSearchRefresh],
	);

	const error = useMemo(
		() => errorProp || new Error('There was an error searching for issues'),
		[errorProp],
	);

	useEffect(() => {
		ff('nin.page-load-instrumentation-fix') && onMount && onMount(error);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		!ff('nin.page-load-instrumentation-fix') &&
			onCaughtIssueSearchError &&
			onCaughtIssueSearchError(error);
	}, [onCaughtIssueSearchError, error]);

	return (
		<Container>
			<EmptyState
				header={formatMessage(messages.emptyStateErrorHeader)}
				description={formatMessage(messages.emptyStateErrorDescription)}
				imageUrl={ff('em-enghealth-consolidate-jfe-assets') ? undefined : NoIssuesGlyph}
				testId="issue-navigator.ui.issue-results.error-view.error-message"
				primaryAction={
					<Button appearance="primary" onClick={onClick}>
						{formatMessage(messages.emptyStateErrorAction)}
					</Button>
				}
				renderImage={() =>
					ff('em-enghealth-consolidate-jfe-assets') ? (
						<Box xcss={ImageWrapperStyles}>
							<SearchNoResultsImage alt="" />
						</Box>
					) : undefined
				}
			/>
			<FireUiAnalytics action="viewed" actionSubject="screen" actionSubjectId="issueSearchError" />
		</Container>
	);
};

const ErrorView = componentWithFF(
	'em-7571-capture-errors-thrown-by-relay-client-and-send-to-splunk_bbvku',
	ErrorViewNew,
	ErrorViewOld,
);

export default ErrorView;

export const RecoverableErrorViewBoundary = componentWithFF(
	'em-7571-capture-errors-thrown-by-relay-client-and-send-to-splunk_bbvku',
	withRecoverableErrorBoundary(
		({ children }: Props & { children: ReactNode; recoveryProp: unknown }) => <>{children}</>,
		{
			recoveryProp: 'recoveryProp',
			Fallback: ErrorViewNew,
		},
	),
	withRecoverableErrorBoundary(
		({ children }: Props & { children: ReactNode; recoveryProp: unknown }) => <>{children}</>,
		{
			recoveryProp: 'recoveryProp',
			Fallback: ErrorViewOld,
		},
	),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexGrow: 1,
	alignItems: 'center',
});

const ImageWrapperStyles = xcss({
	maxHeight: '146px',
	maxWidth: '160px',
	margin: 'auto',
	marginBottom: 'space.300',
});
